import classNames from 'classnames';
import { Dictionary } from 'lodash';
import Moment from 'moment';
import * as React from 'react';
import { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import DisplayLoading from '../../../../common/components/misc/DisplayLoading';
import * as Actions from '../../../../common/services/subscription/Actions';
import * as SubscriptionService from '../../../../common/services/subscription/Service';
import { ChargebeePlanDTO } from '../../../../common/types/dto/ChargebeePlanDTO';
import { ChargebeeSubscriptionDTO } from '../../../../common/types/dto/ChargebeeSubscriptionDTO';
import { CustomerDataDTO } from '../../../../common/types/dto/CustomerDataDTO';
import { SubscriptionItemDTO } from '../../../../common/types/dto/SubscriptionItemDTO';
import { VehicleDataDTO } from '../../../../common/types/dto/VehicleDataDTO';
import { groupBy } from '../../../../common/utils/Utils';
import { RootState } from '../../../base/RootReducer';
import { config } from '../../../common/';
import { useRouter } from '../../../common/RouterHooks';
import { AppThunkDispatch } from '../../../common/types';
import { PeriodUnitEnum, SubscrStatusEnum } from '../Types';
import css from './DynamicTable.scss';

import checkedImg from '../../../assets/images/icons/Azuis/checked.jpg';
import infoImg from '../../../assets/images/icons/Azuis/info.png';

interface BuildDynamicTableState {
    vehicle: VehicleDataDTO | undefined;
    vehcode: number;
    submitErrorText: string;
    toggleDataId: string | null;
}

interface BuildDynamicTableProps {
    getActivePlans: ChargebeePlanDTO[] | undefined;
    getSubscriptionItems: SubscriptionItemDTO[] | undefined;
    getUserSubscriptions: ChargebeeSubscriptionDTO[] | undefined;
    vehcode: number;
    isLanding?: boolean;
    isTugaIframe?: boolean;
}

interface ReduxStateProps {
    customerInfo: CustomerDataDTO | undefined;
    dispatch: AppThunkDispatch;
}

interface ReduxDispatchProps {
    subscriptionActions: typeof Actions;
    subscriptionServiceActions: SubscriptionService.ServiceActions;
}

type BuildDynamicTableProperties =
    ReduxStateProps
    & BuildDynamicTableProps
    & ReduxDispatchProps;

const BuildDynamicTable: React.FC<BuildDynamicTableProperties> = (Props) => {
    const initialState: BuildDynamicTableState = {
        vehicle: undefined,
        vehcode: -1,
        submitErrorText: '',
        toggleDataId: null,
    };
    const [state, setState]: [BuildDynamicTableState, React.Dispatch<any>] = useReducer(
        (oldState, newState) => ({ ...oldState, ...newState }), initialState,
    );
    const { t } = useTranslation();
    const routeProps = useRouter();

    useEffect(() => {
        if (!Props.isLanding && Props.customerInfo) {
            if (Props.customerInfo.vehicles && Props.vehcode !== state.vehcode) {
                const newVehicle = Props.customerInfo.vehicles.find((x) => x.vehcode === Props.vehcode);
                setState({
                    vehcode: Props.vehcode,
                    vehicle: newVehicle,
                });
            }
        }
    }, [Props.customerInfo, Props.vehcode]);

    if (!Props.getUserSubscriptions || !Props.getActivePlans || !Props.getSubscriptionItems || Props.getActivePlans.length === 0 || Props.getSubscriptionItems.length === 0) {
        return (
            <DisplayLoading validation={true} />
        );
    }

    function HandleLandingClick(event: React.MouseEvent, plan: ChargebeePlanDTO) {
        if (Props.isLanding) {
            if (config.IFrameRedirectParent) {
                window.parent.location.href = `${config.IFrameRedirectParent}${config.BaseURL}/subscription/packs`;
            }
            routeProps.history.push(`${config.BaseURL}/subscription/packs`);
            window.location.reload();
        } else if (Props.vehcode < 0) {
            event.preventDefault();
            setState({ submitErrorText: t('subscriptions:noVeh') });
            return;
        } else {
            routeProps.history.push(`${config.BaseURL}/subscription/details/${plan.id}/${Props.vehcode}`);
        }
    }

    const isDevMode = config.AppEnv.indexOf('development') >= 0;
    let isPaymentDue: boolean | undefined = false;
    let hasScheduledChanges: boolean | undefined = false;
    let isNonRenewing: boolean | undefined = false;
    let isActivePlanAlreadyFound: boolean | undefined = false;
    let isAlreadyCanceled: boolean | undefined | null = false;
    let currentTermEndStr: string = '';

    const getSubscriptionItemsGroupBy: Dictionary<SubscriptionItemDTO[]> = groupBy(Props.getSubscriptionItems, 'itemGroupByKey');

    function HandleInfoClick(event: React.MouseEvent) {
        const dataId = event.currentTarget.getAttribute('data-id');
        if (state.toggleDataId) {
            setState({ toggleDataId: null });
        } else {
            setState({ toggleDataId: dataId });
        }
    }

    const BuildNewFrontend: React.FC<BuildDynamicTableProps> = (props) => {
        return (
            <React.Fragment>
                <div className={classNames(['cell', 'large-6', 'small-3', 'text-left'])}>
                    <span className="" />
                </div>

                {props.getActivePlans && props.getActivePlans.map((plan: ChargebeePlanDTO, idx: any) => {
                    return (
                        <div key={`plandiv${plan.id}`} className={classNames(['cell', 'large-auto', 'small-3', 'text-center', 'pacote-prime-table-title'])}>
                            <span>{plan.name}</span>
                        </div>
                    );
                })}
                {getSubscriptionItemsGroupBy && Object.entries(getSubscriptionItemsGroupBy)
                    .map(([key, value]) => {
                        const dataId = `dataid${value[0].code}`;
                        return (
                            <React.Fragment key={`itemdivTop${key}`}>
                                <div className={classNames(['cell', 'large-6  small-3', 'text-center', 'pacote-prime-table-name'])}>
                                    <div className={'media-object'}>
                                        <div className={'media-object-section'}>
                                            {value[0].ref === 'REF11' && <span className={'notify-badge'}>NOVIDADE</span>}
                                            <div className={'thumbnail'}>
                                                <img src={config.SubscrPlanitemsImagesPath + value[0].icon} />
                                            </div>
                                        </div>
                                        <div className={classNames(['media-object-section', 'main-section', 'align-self-middle'])}>
                                            <span>{value[0].name}</span>
                                        </div>
                                        <div className={classNames(['media-object-section', 'align-self-middle'])}>
                                            <div className={'thumbnail'}>
                                                <img className={'info'} data-id={dataId} src={infoImg} onClick={(e) => HandleInfoClick(e)} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {props.getActivePlans && props.getActivePlans.map((plan: ChargebeePlanDTO, idx: any) => {
                                    const userSubscription = Props.getUserSubscriptions && Props.getUserSubscriptions.find((x) => x.planBaseId === plan.id &&
                                        x.status !== SubscrStatusEnum.Cancelled && x.status !== SubscrStatusEnum.Paused && x.status !== SubscrStatusEnum.UnKnown && x.vehcode === Props.vehcode);
                                    const userSubscriptionActivePlan = isActivePlanAlreadyFound ? undefined : userSubscription ? userSubscription : Props.getUserSubscriptions && Props.getUserSubscriptions.find((x) => x.planBaseId === plan.id &&
                                        x.status !== SubscrStatusEnum.Paused && x.status !== SubscrStatusEnum.UnKnown && x.vehcode === Props.vehcode);
                                    const isActivePlan = userSubscription && (isDevMode || userSubscription.vehcode === Props.vehcode);
                                    isActivePlanAlreadyFound = isActivePlanAlreadyFound || isActivePlan;
                                    isPaymentDue = isPaymentDue || (isActivePlan && userSubscription && userSubscription.isDue);
                                    hasScheduledChanges = hasScheduledChanges || (isActivePlan && userSubscription && userSubscription.hasScheduledChanges);
                                    isNonRenewing = isNonRenewing || (isActivePlan && userSubscription && userSubscription.status === SubscrStatusEnum.NonRenewing);
                                    isAlreadyCanceled = isNonRenewing && userSubscriptionActivePlan && userSubscriptionActivePlan.currentTermEnd && (new Date(userSubscriptionActivePlan.currentTermEnd)).getTime() <= (new Date()).getTime();
                                    currentTermEndStr = currentTermEndStr && !isActivePlan ? currentTermEndStr : userSubscriptionActivePlan && userSubscriptionActivePlan.currentTermEnd ? Moment(userSubscriptionActivePlan.currentTermEnd).format('DD/MM/YYYY HH:mm:ss') : t('subscriptions:unknownDate');
                                    const colorClass = isActivePlan && !isAlreadyCanceled ? (isPaymentDue || hasScheduledChanges || isNonRenewing) ? css.orange : css.blue : null;

                                    let itemFound: SubscriptionItemDTO | undefined;
                                    value.map((x: SubscriptionItemDTO) => {
                                        const exists = plan && plan.availableServices && plan.availableServices.indexOf(x.code) > -1;
                                        if (exists) {
                                            itemFound = x;
                                        }
                                    });

                                    return (
                                        <React.Fragment key={`itemdivMid${idx}`}>

                                            <div className={classNames(['cell', 'large-auto', 'small-3', 'text-center', 'pacote-prime-table-value', colorClass])}>
                                                <div className={'media-object'}>
                                                    <div className={classNames(['media-object-section', 'main-section', 'align-self-middle'])}>
                                                        <div className={classNames(['thumbnail', 'align-center-middle'])}>
                                                            {itemFound ? (itemFound.checkAltText ? <span className={itemFound.checkAltText.length > 5 ? 'descontolongo' : 'desconto'}>{itemFound.checkAltText}</span> : <img src={checkedImg} />) : ''}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    );
                                })}

                                <div className={classNames(['cell', 'large-6', 'pacote-prime-table-name-info', 'align-middle'])} data-id={dataId} style={{ display: state.toggleDataId === dataId ? 'block' : 'none' }}>
                                    <span>{value[0].description}</span>
                                </div>
                                <div className={classNames(['cell', 'large-6', 'pacote-prime-table-name-info', 'align-middle', 'fundo-branco'])} data-id={dataId} style={{ display: state.toggleDataId === dataId ? 'block' : 'none' }} />
                            </React.Fragment>
                        );
                    })}

                <div className={classNames(['cell', 'large-6', 'small-3', 'text-left'])}>
                    <span />
                </div>
                {props.getActivePlans && props.getActivePlans.map((plan: ChargebeePlanDTO, idx: any) => {
                    const yearlyPlan = plan && plan.plans && plan.plans.find((x) => x.periodUnit === PeriodUnitEnum.Year);
                    const monthlyPlan = plan && plan.plans && plan.plans.find((x) => x.periodUnit === PeriodUnitEnum.Month);
                    const fPlan = monthlyPlan ? monthlyPlan : yearlyPlan;
                    return (
                        <div key={`pricediv${plan.id}`} className={classNames(['cell', 'large-auto', 'small-3', 'text-left', 'pacote-prime-table-price'])}>
                            <p aria-describedby="classiccare">{`${fPlan ? fPlan.priceFormated : '--,--'}€`}</p>
                            <span id="classiccare">{t('subscriptions:monthPriceFooter')}</span>
                        </div>
                    );
                })}
                <div className={classNames(['cell', 'large-6', 'small-3', 'text-left'])}>
                    <span />
                </div>

                {!Props.isTugaIframe && props.getActivePlans && props.getActivePlans.map((plan: ChargebeePlanDTO, idx: any) => {
                    const userSubscription = Props.getUserSubscriptions && Props.getUserSubscriptions.find((x) => x.planBaseId === plan.id &&
                        x.status !== SubscrStatusEnum.Cancelled && x.status !== SubscrStatusEnum.Paused && x.status !== SubscrStatusEnum.UnKnown && x.vehcode === Props.vehcode);
                    const isActivePlan = userSubscription && (isDevMode || userSubscription.vehcode === Props.vehcode);
                    const showDetailsMessage = isActivePlan && !isAlreadyCanceled;
                    return (
                        <div key={`buttonDiv${plan.id}`} className={classNames(['cell', 'large-auto', 'small-3', 'text-left', 'pacote-prime-table-subscrever'])}>
                            <button type="button" className={classNames(['button', 'expanded'])} onClick={(e) => HandleLandingClick(e, plan)}>
                                {Props.isLanding || !showDetailsMessage ? t('subscriptions:subscribe') : t('subscriptions:details')}
                            </button>
                            {state.submitErrorText && <div className="error-message" style={{ float: 'right' }}>{state.submitErrorText}</div>}
                        </div>
                    );
                })}

            </React.Fragment>
        );
    };

    return (
        <React.Fragment>
            {!Props.isTugaIframe && <div className={classNames(['cell', 'text-left'])}>
                <span className={'pacote-prime-title'}>{t('subscriptions:note')}</span>
            </div>}
            {BuildNewFrontend(Props)}
        </React.Fragment>
    );
};

const mapStateToProps = (store: RootState) => ({
    customerInfo: store.customerState.customerData.data.customerData,
    seriesSegments: store.appState.lookupValues.seriesSegments,
});
const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
    subscriptionActions: bindActionCreators(Actions, dispatch),
    subscriptionServiceActions: SubscriptionService.bindServiceActionCreators(config, dispatch),
    dispatch,
});
export default connect(mapStateToProps, mapDispatchToProps)(BuildDynamicTable);
